import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import CustomerNav from "@/components/layout/CustomerNav";
import { customerApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Lock,
  ShieldCheck,
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  isLongevityUnlocked,
  LONGEVITY_ONBOARDING_STEPS,
  DIAGNOSES_OPTIONS,
  SYMPTOMS_OPTIONS,
  ANCESTRY_OPTIONS,
  GUT_INVESTIGATION_OPTIONS,
  REGULAR_DIET_OPTIONS,
  EATING_PATTERNS_OPTIONS,
  RECREATIONAL_DRUGS_OPTIONS,
} from "@/lib/LongevityEngine";

/**
 * Longevity Journey Onboarding Wizard (Iter-370).
 *
 * 10-step deep-history intake. All data persists to
 * `db.customer_longevity_records` (one doc per customer) via
 * `PUT /api/longevity/me/onboarding` — the whole payload is upserted
 * on every step-forward so the guest can close the tab and pick up
 * later. Documents (Step 9) are streamed to Emergent Object Storage
 * via `POST /api/longevity/me/onboarding/upload`.
 *
 * Whitelist gate (Iter-367): the entire route silently redirects to
 * `/portal/profile` when the signed-in email is not on the ring.
 */

const MAX_FILE_MB = 50;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const ACCEPT_MIME = "application/pdf,image/*";

function StepShell({ title, subtitle, children }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-editorial text-2xl sm:text-3xl mb-1" data-testid="longevity-onboarding-step-title">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function TextRow({ label, value, onChange, testId, placeholder, multiline }) {
  return (
    <div>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {multiline ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder={placeholder || ""}
          className="mt-1 rounded-lg"
          rows={3}
        />
      ) : (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder={placeholder || ""}
          className="mt-1 rounded-lg"
        />
      )}
    </div>
  );
}

function ScaleRow({ label, value, onChange, testId }) {
  const v = typeof value === "number" ? value : 5;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
        <span className="text-xs font-medium text-primary" data-testid={`${testId}-value`}>{v}/10</span>
      </div>
      <Slider
        value={[v]}
        min={1}
        max={10}
        step={1}
        onValueChange={(vals) => onChange(vals[0])}
        data-testid={testId}
      />
    </div>
  );
}

function MultiCheck({ label, options, values, onChange, testIdPrefix }) {
  const set = new Set(values || []);
  const toggle = (opt) => {
    const next = new Set(set);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange(Array.from(next));
  };
  return (
    <div>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((opt) => (
          <label
            key={opt}
            className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer hover:bg-muted/50"
          >
            <Checkbox
              checked={set.has(opt)}
              onCheckedChange={() => toggle(opt)}
              data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------
function Step1Consent({ form, update }) {
  const consent = form.consent || {};
  return (
    <StepShell
      title="Consent Information"
      subtitle="Before we proceed, please review the following information about how your data will be used and your rights."
    >
      <Card className="rounded-2xl border-primary/20 bg-primary/5">
        <CardContent className="p-5 sm:p-6 space-y-3">
          <p className="text-sm font-semibold" data-testid="longevity-consent-bold">
            Your privacy is everything. Your answers are encrypted and stored securely.
            You own your data. We will never share it or use it without your full consent.
          </p>
          <Link
            to="/privacy-longevity"
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline hover:no-underline inline-flex items-center gap-1"
            data-testid="longevity-consent-privacy-link"
          >
            <ShieldCheck className="w-4 h-4" /> Review the full privacy policy →
          </Link>
        </CardContent>
      </Card>

      <label className="flex items-start gap-3 border rounded-lg p-4 cursor-pointer">
        <Checkbox
          className="mt-0.5"
          checked={!!consent.agreed}
          onCheckedChange={(v) => update("consent", { ...consent, agreed: !!v, agreed_at: v ? new Date().toISOString() : null })}
          data-testid="longevity-consent-checkbox"
        />
        <span className="text-sm">
          I have read the privacy policy and I consent to share my answer with Cocoon Medical Spa Team.
        </span>
      </label>
    </StepShell>
  );
}

function Step2Identity({ form, update }) {
  const id = form.identity || {};
  const set = (k, v) => update("identity", { ...id, [k]: v });
  return (
    <StepShell
      title="Your Identity"
      subtitle="These details are pre-filled from your profile. Update anything that has changed — the changes sync back to your Cocoon profile automatically."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextRow label="First name" value={id.first_name} onChange={(v) => set("first_name", v)} testId="longevity-id-first-name" />
        <TextRow label="Last name" value={id.last_name} onChange={(v) => set("last_name", v)} testId="longevity-id-last-name" />
        <div>
          <Label className="text-xs font-medium text-muted-foreground">Email</Label>
          <Input
            value={id.email || ""}
            readOnly
            disabled
            className="mt-1 rounded-lg bg-muted"
            data-testid="longevity-id-email"
          />
          <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
            <Lock className="w-3 h-3" /> Email is your sign-in — contact the front desk to change it.
          </p>
        </div>
        <TextRow label="WhatsApp / Mobile" value={id.whatsapp} onChange={(v) => set("whatsapp", v)} testId="longevity-id-whatsapp" placeholder="+62..." />
        <div>
          <Label className="text-xs font-medium text-muted-foreground">Date of birth</Label>
          <Input
            type="date"
            value={id.birthday || ""}
            onChange={(e) => set("birthday", e.target.value)}
            className="mt-1 rounded-lg"
            data-testid="longevity-id-birthday"
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-muted-foreground">Gender</Label>
          <Select value={id.gender || ""} onValueChange={(v) => set("gender", v)}>
            <SelectTrigger className="mt-1 rounded-lg" data-testid="longevity-id-gender">
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Non-binary">Non-binary</SelectItem>
              <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </StepShell>
  );
}

function Step3PersonalContext({ form, update }) {
  const pc = form.personal_context || {};
  const set = (k, v) => update("personal_context", { ...pc, [k]: v });
  const ec = pc.emergency_contact || {};
  const setEc = (k, v) => set("emergency_contact", { ...ec, [k]: v });
  const meds = pc.medications || {};
  const setMeds = (k, v) => set("medications", { ...meds, [k]: v });

  return (
    <StepShell
      title="Personal Context"
      subtitle="A snapshot of you today — how you feel, what you take, and what shaped your health so far."
    >
      <Card className="rounded-xl">
        <CardContent className="p-4 space-y-3">
          <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Emergency contact</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextRow label="Name" value={ec.name} onChange={(v) => setEc("name", v)} testId="longevity-ec-name" />
            <TextRow label="Relationship" value={ec.relationship} onChange={(v) => setEc("relationship", v)} testId="longevity-ec-relationship" />
            <TextRow label="Email" value={ec.email} onChange={(v) => setEc("email", v)} testId="longevity-ec-email" />
            <TextRow label="Phone" value={ec.phone} onChange={(v) => setEc("phone", v)} testId="longevity-ec-phone" />
          </div>
        </CardContent>
      </Card>

      <TextRow label="Main reason for starting your longevity journey" value={pc.main_reason} onChange={(v) => set("main_reason", v)} testId="longevity-pc-main-reason" multiline />
      <TextRow label="Describe a time when you felt at your best — when was it and what was different?" value={pc.feeling_best_history} onChange={(v) => set("feeling_best_history", v)} testId="longevity-pc-feeling-best" multiline />
      <TextRow label="Occupation / Daily activity" value={pc.occupation} onChange={(v) => set("occupation", v)} testId="longevity-pc-occupation" multiline />
      <ScaleRow label="Overall how do you feel today?" value={pc.overall_feeling} onChange={(v) => set("overall_feeling", v)} testId="longevity-pc-overall-feeling" />

      <MultiCheck label="Existing diagnoses" options={DIAGNOSES_OPTIONS} values={pc.diagnoses} onChange={(v) => set("diagnoses", v)} testIdPrefix="longevity-pc-diagnosis" />

      <TextRow label="Conception (were you trying, IVF, surrogate, natural, notes?)" value={pc.conception} onChange={(v) => set("conception", v)} testId="longevity-pc-conception" multiline />

      <Card className="rounded-xl">
        <CardContent className="p-4 space-y-3">
          <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Medications</p>
          <TextRow label="Prescription medications" value={meds.prescription} onChange={(v) => setMeds("prescription", v)} testId="longevity-pc-meds-rx" multiline />
          <TextRow label="Over-the-counter medications" value={meds.otc} onChange={(v) => setMeds("otc", v)} testId="longevity-pc-meds-otc" multiline />
          <TextRow label="Supplements" value={meds.supplements} onChange={(v) => setMeds("supplements", v)} testId="longevity-pc-meds-supplements" multiline />
        </CardContent>
      </Card>

      <TextRow label="Allergies (medications, foods, environment)" value={pc.allergies} onChange={(v) => set("allergies", v)} testId="longevity-pc-allergies" multiline />
      <TextRow label="Hormone issues (thyroid, oestrogen, testosterone, PCOS, menopause…)" value={pc.hormone_issues} onChange={(v) => set("hormone_issues", v)} testId="longevity-pc-hormone-issues" multiline />
      <TextRow label="Dental history / silver fillings (amalgams, root canals, extractions)" value={pc.dental_history} onChange={(v) => set("dental_history", v)} testId="longevity-pc-dental" multiline />
    </StepShell>
  );
}

function Step4BodyAncestry({ form, update }) {
  const ba = form.body_ancestry || {};
  const set = (k, v) => update("body_ancestry", { ...ba, [k]: v });
  return (
    <StepShell
      title="Body & Ancestry"
      subtitle="Symptoms you're carrying today plus the family and childhood history that shaped them."
    >
      <MultiCheck label="Ongoing symptoms" options={SYMPTOMS_OPTIONS} values={ba.symptoms} onChange={(v) => set("symptoms", v)} testIdPrefix="longevity-ba-symptom" />

      <div>
        <Label className="text-xs font-medium text-muted-foreground">Ancestry</Label>
        <Select value={ba.ancestry || ""} onValueChange={(v) => set("ancestry", v)}>
          <SelectTrigger className="mt-1 rounded-lg" data-testid="longevity-ba-ancestry">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {ANCESTRY_OPTIONS.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <TextRow label="Family illness patterns (parents, siblings, grandparents)" value={ba.family_illness} onChange={(v) => set("family_illness", v)} testId="longevity-ba-family" multiline />
      <TextRow label="Exercise frequency & type" value={ba.exercise} onChange={(v) => set("exercise", v)} testId="longevity-ba-exercise" multiline />
      <TextRow label="Living situation (city/rural, home, roommates, stressors)" value={ba.living_situation} onChange={(v) => set("living_situation", v)} testId="longevity-ba-living" multiline />
      <TextRow label="Mother's pregnancy history (complications, medications, notes)" value={ba.mother_pregnancy} onChange={(v) => set("mother_pregnancy", v)} testId="longevity-ba-mother" multiline />
      <TextRow label="Parents' smoking / vaping history" value={ba.parents_smoking} onChange={(v) => set("parents_smoking", v)} testId="longevity-ba-parents-smoking" multiline />
      <TextRow label="Childhood health issues & antibiotic use" value={ba.childhood_health} onChange={(v) => set("childhood_health", v)} testId="longevity-ba-childhood" multiline />
      <TextRow label="Regular aches or pains" value={ba.aches_pains} onChange={(v) => set("aches_pains", v)} testId="longevity-ba-aches" multiline />
    </StepShell>
  );
}

function Step5GutHealth({ form, update }) {
  const gh = form.gut_health || {};
  const set = (k, v) => update("gut_health", { ...gh, [k]: v });
  return (
    <StepShell
      title="Gut Health"
      subtitle="Digestion, diet patterns, and specific foods that support (or challenge) your gut."
    >
      <TextRow label="Describe your digestion" value={gh.digestion} onChange={(v) => set("digestion", v)} testId="longevity-gh-digestion" multiline />
      <MultiCheck label="Gut investigations you've had" options={GUT_INVESTIGATION_OPTIONS} values={gh.investigations} onChange={(v) => set("investigations", v)} testIdPrefix="longevity-gh-investigation" />
      <TextRow label="Food sensitivities / reactions" value={gh.food_sensitivities} onChange={(v) => set("food_sensitivities", v)} testId="longevity-gh-food-sensitivities" multiline />
      <MultiCheck label="Regular diet items" options={REGULAR_DIET_OPTIONS} values={gh.regular_diet} onChange={(v) => set("regular_diet", v)} testIdPrefix="longevity-gh-diet" />
      <TextRow label="Typical meal / snack timeline (morning → night)" value={gh.meal_timeline} onChange={(v) => set("meal_timeline", v)} testId="longevity-gh-meal-timeline" multiline />
      <MultiCheck label="Special eating patterns" options={EATING_PATTERNS_OPTIONS} values={gh.eating_patterns} onChange={(v) => set("eating_patterns", v)} testIdPrefix="longevity-gh-eating-pattern" />

      <Card className="rounded-xl">
        <CardContent className="p-4 space-y-3">
          <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Specific intake</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextRow label="Iodized salt (frequency)" value={gh.iodized_salt} onChange={(v) => set("iodized_salt", v)} testId="longevity-gh-iodized-salt" />
            <TextRow label="Himalayan salt (frequency)" value={gh.himalayan_salt} onChange={(v) => set("himalayan_salt", v)} testId="longevity-gh-himalayan" />
            <TextRow label="Brazil nuts (frequency)" value={gh.brazil_nuts} onChange={(v) => set("brazil_nuts", v)} testId="longevity-gh-brazil" />
            <TextRow label="Beef liver (frequency)" value={gh.beef_liver} onChange={(v) => set("beef_liver", v)} testId="longevity-gh-beef-liver" />
            <TextRow label="Red meat (frequency)" value={gh.red_meat} onChange={(v) => set("red_meat", v)} testId="longevity-gh-red-meat" />
            <TextRow label="Fish (frequency)" value={gh.fish} onChange={(v) => set("fish", v)} testId="longevity-gh-fish" />
            <TextRow label="Daily water volume (L)" value={gh.water_volume} onChange={(v) => set("water_volume", v)} testId="longevity-gh-water" />
            <TextRow label="Daily soft drink volume" value={gh.soft_drink_volume} onChange={(v) => set("soft_drink_volume", v)} testId="longevity-gh-soft-drink" />
          </div>
        </CardContent>
      </Card>
    </StepShell>
  );
}

function Step6MindEnergy({ form, update }) {
  const me = form.mind_energy || {};
  const set = (k, v) => update("mind_energy", { ...me, [k]: v });
  return (
    <StepShell
      title="Mind & Energy"
      subtitle="Your rhythm, mood, focus, and stress — a baseline your clinician can improve."
    >
      <ScaleRow label="Energy level" value={me.energy_level} onChange={(v) => set("energy_level", v)} testId="longevity-me-energy-level" />
      <TextRow label="Daily energy peak / low (what time and why?)" value={me.energy_peak_low} onChange={(v) => set("energy_peak_low", v)} testId="longevity-me-energy-peak-low" multiline />
      <TextRow label="Sleep & wake patterns" value={me.sleep_wake} onChange={(v) => set("sleep_wake", v)} testId="longevity-me-sleep" multiline />
      <ScaleRow label="Mood today" value={me.mood} onChange={(v) => set("mood", v)} testId="longevity-me-mood" />
      <TextRow label="Anxiety / depression history" value={me.anxiety_depression} onChange={(v) => set("anxiety_depression", v)} testId="longevity-me-anxiety" multiline />
      <TextRow label="Meditation / mindfulness practices" value={me.meditation} onChange={(v) => set("meditation", v)} testId="longevity-me-meditation" multiline />
      <ScaleRow label="Thinking clarity" value={me.thinking_clarity} onChange={(v) => set("thinking_clarity", v)} testId="longevity-me-thinking" />
      <TextRow label="Brain fog details (when, triggers, duration)" value={me.brain_fog} onChange={(v) => set("brain_fog", v)} testId="longevity-me-brain-fog" multiline />
      <ScaleRow label="Stress level" value={me.stress} onChange={(v) => set("stress", v)} testId="longevity-me-stress" />
      <TextRow label="Head injury history" value={me.head_injury} onChange={(v) => set("head_injury", v)} testId="longevity-me-head-injury" multiline />
    </StepShell>
  );
}

function Step7Lifestyle({ form, update }) {
  const ls = form.lifestyle || {};
  const set = (k, v) => update("lifestyle", { ...ls, [k]: v });
  return (
    <StepShell
      title="Lifestyle & Environment"
      subtitle="Everyday exposures around your food, water, and body that add up over decades."
    >
      <TextRow label="Cooking oils typically used" value={ls.cooking_oils} onChange={(v) => set("cooking_oils", v)} testId="longevity-ls-oils" multiline />
      <TextRow label="Plastic bottles / containers / spatulas — how often?" value={ls.plastic_use} onChange={(v) => set("plastic_use", v)} testId="longevity-ls-plastic" multiline />
      <div>
        <Label className="text-xs font-medium text-muted-foreground">Pot material</Label>
        <Select value={ls.pot_material || ""} onValueChange={(v) => set("pot_material", v)}>
          <SelectTrigger className="mt-1 rounded-lg" data-testid="longevity-ls-pot-material">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Teflon / Non-stick">Teflon / Non-stick</SelectItem>
            <SelectItem value="Stainless steel">Stainless steel</SelectItem>
            <SelectItem value="Cast iron">Cast iron</SelectItem>
            <SelectItem value="Ceramic">Ceramic</SelectItem>
            <SelectItem value="Glass">Glass</SelectItem>
            <SelectItem value="Mixed">Mixed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <TextRow label="Water filter type (RO, carbon, none…)" value={ls.water_filter} onChange={(v) => set("water_filter", v)} testId="longevity-ls-water-filter" />
      <TextRow label="Toxin exposure history (mould, pesticides, heavy metals, industrial)" value={ls.toxin_exposure} onChange={(v) => set("toxin_exposure", v)} testId="longevity-ls-toxin" multiline />
      <MultiCheck label="Recreational drug use" options={RECREATIONAL_DRUGS_OPTIONS} values={ls.recreational_drugs} onChange={(v) => set("recreational_drugs", v)} testIdPrefix="longevity-ls-drug" />
      <TextRow label="Smoking / vaping history" value={ls.smoking_vaping} onChange={(v) => set("smoking_vaping", v)} testId="longevity-ls-smoking" multiline />
      <TextRow label="Alcohol consumption (units/week, type)" value={ls.alcohol_volume} onChange={(v) => set("alcohol_volume", v)} testId="longevity-ls-alcohol" multiline />
    </StepShell>
  );
}

function Step8Goals({ form, update }) {
  const g = form.goals || {};
  const set = (k, v) => update("goals", { ...g, [k]: v });
  return (
    <StepShell
      title="Your Goals"
      subtitle="What success looks like — so we can measure the change together."
    >
      <TextRow label="Most important change you want to see" value={g.most_important_change} onChange={(v) => set("most_important_change", v)} testId="longevity-goals-most-important" multiline />
      <TextRow label="Your 6-month vision" value={g.six_month_vision} onChange={(v) => set("six_month_vision", v)} testId="longevity-goals-vision" multiline />
      <TextRow label="Obstacles you anticipate" value={g.obstacles} onChange={(v) => set("obstacles", v)} testId="longevity-goals-obstacles" multiline />
      <TextRow label="Your support network" value={g.support_network} onChange={(v) => set("support_network", v)} testId="longevity-goals-support" multiline />
      <TextRow label="Honest concerns about this journey" value={g.honest_concerns} onChange={(v) => set("honest_concerns", v)} testId="longevity-goals-concerns" multiline />
      <TextRow label="Recent weight changes (any direction)" value={g.weight_changes} onChange={(v) => set("weight_changes", v)} testId="longevity-goals-weight" multiline />
    </StepShell>
  );
}

function Step9Documents({ record, onUploaded, onRemoved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pickFile = () => fileRef.current?.click();

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-uploading the same file works
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`File too large — max ${MAX_FILE_MB} MB per file.`);
      return;
    }
    const okType = file.type === "application/pdf" || file.type.startsWith("image/");
    if (!okType) {
      toast.error("Only PDF and image files are supported.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await customerApi.post("/longevity/me/onboarding/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(r.data?.record);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      const r = await customerApi.delete(`/longevity/me/onboarding/documents/${id}`);
      onRemoved(r.data?.record);
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const docs = record?.documents || [];

  return (
    <StepShell
      title="Documents"
      subtitle={`Upload lab reports, imaging, prescriptions or any records you'd like your clinician to see. PDF and images only — max ${MAX_FILE_MB} MB per file.`}
    >
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT_MIME}
        onChange={handleChange}
        className="hidden"
        data-testid="longevity-docs-hidden-input"
      />
      <Button
        type="button"
        variant="outline"
        onClick={pickFile}
        disabled={busy}
        className="rounded-full"
        data-testid="longevity-docs-upload-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {busy ? "Uploading…" : "Add a document"}
      </Button>

      <div className="space-y-2">
        {docs.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="longevity-docs-empty">
            No documents yet. Upload is optional — you can also add them later.
          </p>
        ) : (
          docs.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-3 border rounded-lg p-3"
              data-testid={`longevity-doc-${d.id}`}
            >
              <div className="p-2 rounded-full bg-muted flex-shrink-0">
                {d.content_type?.startsWith("image/") ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{d.filename}</p>
                <p className="text-[10px] text-muted-foreground">
                  {(d.size / (1024 * 1024)).toFixed(2)} MB · {d.content_type}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove(d.id)}
                className="text-muted-foreground hover:text-destructive"
                data-testid={`longevity-doc-remove-${d.id}`}
                aria-label="Remove document"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </StepShell>
  );
}

function Step10Review({ record }) {
  const docs = record?.documents || [];
  return (
    <StepShell
      title="Review & Submit"
      subtitle="One last look. Once submitted, our qualified healthcare team will review your responses before your visit."
    >
      <Card className="rounded-2xl border-emerald-200 bg-emerald-50/50">
        <CardContent className="p-5 flex items-start gap-3">
          <div className="p-2 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-900" data-testid="longevity-review-encrypted-note">
              Your data is encrypted and will be reviewed by qualified healthcare professionals.
            </p>
            <p className="text-xs text-emerald-900/80 mt-1">
              You can return to this wizard anytime to edit or add more information.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground space-y-1">
        <p>Documents attached: <b>{docs.length}</b></p>
        <p>Status: <b className="capitalize">{record?.status || "draft"}</b></p>
        {record?.submitted_at && (
          <p>Last submitted: <b>{new Date(record.submitted_at).toLocaleString()}</b></p>
        )}
      </div>
    </StepShell>
  );
}

// ---------------------------------------------------------------------------
// Wizard shell
// ---------------------------------------------------------------------------
export default function LongevityOnboarding() {
  const { customer } = useAuth();
  const nav = useNavigate();
  const unlocked = isLongevityUnlocked(customer?.email);

  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({
    consent: {},
    identity: {},
    personal_context: {},
    body_ancestry: {},
    gut_health: {},
    mind_energy: {},
    lifestyle: {},
    goals: {},
  });
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!unlocked) return;
    setLoading(true);
    try {
      const r = await customerApi.get("/longevity/me/onboarding");
      const rec = r.data?.record;
      setRecord(rec);
      setForm({
        consent: rec?.consent || {},
        identity: rec?.identity || {},
        personal_context: rec?.personal_context || {},
        body_ancestry: rec?.body_ancestry || {},
        gut_health: rec?.gut_health || {},
        mind_energy: rec?.mind_energy || {},
        lifestyle: rec?.lifestyle || {},
        goals: rec?.goals || {},
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [unlocked]);

  useEffect(() => { load(); }, [load]);

  const update = (section, value) => setForm((prev) => ({ ...prev, [section]: value }));

  const saveDraft = async (nextStep) => {
    setBusy(true);
    try {
      const r = await customerApi.put("/longevity/me/onboarding", form);
      setRecord(r.data?.record);
      if (typeof nextStep === "number") setStep(nextStep);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await customerApi.put("/longevity/me/onboarding", form);
      const r = await customerApi.post("/longevity/me/onboarding/submit");
      setRecord(r.data?.record);
      toast.success("Submitted · our team will review before your visit");
      nav("/portal/longevity");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!unlocked) return <Navigate to="/portal/profile" replace />;

  const consentAgreed = !!form.consent?.agreed;
  const canAdvance = step > 0 ? true : consentAgreed;

  const totalSteps = LONGEVITY_ONBOARDING_STEPS.length;
  const progress = ((step + 1) / totalSteps) * 100;
  const isLast = step === totalSteps - 1;

  let stepNode = null;
  switch (step) {
    case 0: stepNode = <Step1Consent form={form} update={update} />; break;
    case 1: stepNode = <Step2Identity form={form} update={update} />; break;
    case 2: stepNode = <Step3PersonalContext form={form} update={update} />; break;
    case 3: stepNode = <Step4BodyAncestry form={form} update={update} />; break;
    case 4: stepNode = <Step5GutHealth form={form} update={update} />; break;
    case 5: stepNode = <Step6MindEnergy form={form} update={update} />; break;
    case 6: stepNode = <Step7Lifestyle form={form} update={update} />; break;
    case 7: stepNode = <Step8Goals form={form} update={update} />; break;
    case 8: stepNode = <Step9Documents record={record} onUploaded={(rec) => setRecord(rec)} onRemoved={(rec) => setRecord(rec)} />; break;
    case 9: stepNode = <Step10Review record={record} />; break;
    default: stepNode = null;
  }

  return (
    <div>
      <CustomerNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button
          type="button"
          onClick={() => nav("/portal/longevity")}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
          data-testid="longevity-onboarding-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </button>

        <p className="tracking-overline text-[11px] text-muted-foreground">LONGEVITY ONBOARDING</p>
        <h1 className="font-editorial text-4xl sm:text-5xl mt-2 mb-3" data-testid="longevity-onboarding-heading">
          Your Longevity Journey
        </h1>

        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="longevity-onboarding-step-label">
            Step {step + 1} of {totalSteps} · {LONGEVITY_ONBOARDING_STEPS[step]}
          </span>
          {record?.status === "submitted" && (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="w-3 h-3" /> Submitted
            </span>
          )}
        </div>
        <Progress value={progress} className="h-1 mb-6" data-testid="longevity-onboarding-progress" />

        <Card className="rounded-2xl">
          <CardContent className="p-5 sm:p-7">
            {loading ? (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="longevity-onboarding-loading">
                Loading your onboarding…
              </p>
            ) : (
              stepNode
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between mt-6 gap-3">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            disabled={step === 0 || busy}
            onClick={() => saveDraft(step - 1)}
            data-testid="longevity-onboarding-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>

          {isLast ? (
            <Button
              type="button"
              className="rounded-full"
              disabled={busy || !consentAgreed}
              onClick={submit}
              data-testid="longevity-onboarding-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Submit for review
            </Button>
          ) : (
            <Button
              type="button"
              className="rounded-full"
              disabled={busy || !canAdvance}
              onClick={() => saveDraft(step + 1)}
              data-testid="longevity-onboarding-next-btn"
            >
              {busy ? "Saving…" : "Save & continue"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {!consentAgreed && step === 0 && (
          <p className="text-[11px] text-muted-foreground text-center mt-3">
            Tick the consent box to continue.
          </p>
        )}
      </div>
    </div>
  );
}
